import React, { useState, useEffect } from "react";
import { Search, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight, Send, User, Calendar, Tag } from "lucide-react";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import api from "../../../api/axiosInstance"; // ⚠️ verify path relative to this file
import '../../../assets/css/admin/supporttickets.css';

// Backend field names → component field names
const mapTicket = (t) => ({
  dbId: t.id,
  id: t.ticket_code,
  subject: t.subject,
  category: t.category,
  priority: t.priority,
  status: t.status,
  user: t.user_name,
  userType: t.user_type,
  createdAt: new Date(t.created_at).toLocaleString(),
  updatedAt: new Date(t.updated_at).toLocaleString(),
  description: t.description,
  replies: (t.replies || []).map(r => ({
    from: r.from,
    message: r.message,
    time: new Date(r.created_at).toLocaleString(),
  })),
});

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [filterCategory, setFilterCategory] = useState("All Categories");
  const [filterPriority, setFilterPriority] = useState("All Priorities");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch tickets from backend
  useEffect(() => {
    let cancelled = false;

    const fetchTickets = async () => {
      setTicketsLoading(true);
      setTicketsError(null);
      try {
        const res = await api.get("/support-tickets");
        if (!cancelled) {
          setTickets(res.data.map(mapTicket));
        }
      } catch (err) {
        console.error("Failed to load tickets:", err);
        if (!cancelled) {
          setTicketsError("Could not load support tickets. Please try again later.");
        }
      } finally {
        if (!cancelled) setTicketsLoading(false);
      }
    };

    fetchTickets();
    return () => { cancelled = true; };
  }, []);

  const filteredTickets = tickets
    .filter(t => activeTab === "All" || t.status === activeTab)
    .filter(t => filterCategory === "All Categories" || t.category === filterCategory)
    .filter(t => filterPriority === "All Priorities" || t.priority === filterPriority)
    .filter(t =>
      t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, startIndex + itemsPerPage);

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === "Open").length,
    inProgress: tickets.filter(t => t.status === "In Progress").length,
    resolved: tickets.filter(t => t.status === "Resolved").length,
    closed: tickets.filter(t => t.status === "Closed").length
  };

  const handleStatusChange = (ticket, newStatus) => {
    Swal.fire({
      title: `Change Status to ${newStatus}?`,
      html: `Are you sure you want to change ticket <strong>${ticket.id}</strong> status?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Change',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#1e3a5f',
      cancelButtonColor: '#64748b'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.patch(`/support-tickets/${ticket.dbId}/status`, { status: newStatus });

          const updatedTickets = tickets.map(t =>
            t.dbId === ticket.dbId ? { ...t, status: newStatus, updatedAt: new Date().toLocaleString() } : t
          );
          setTickets(updatedTickets);

          if (selectedTicket?.dbId === ticket.dbId) {
            setSelectedTicket({ ...selectedTicket, status: newStatus });
          }

          Swal.fire({
            title: 'Status Updated!',
            html: `Ticket status changed to <strong>${newStatus}</strong>`,
            icon: 'success',
            confirmButtonColor: '#1e3a5f',
            timer: 2000
          });
        } catch (err) {
          console.error("Failed to update status:", err);
          Swal.fire({ title: 'Error', text: 'Could not update status. Please try again.', icon: 'error', confirmButtonColor: '#ea580c' });
        }
      }
    });
  };

  const handlePriorityChange = async (ticket, newPriority) => {
    try {
      await api.patch(`/support-tickets/${ticket.dbId}/priority`, { priority: newPriority });

      const updatedTickets = tickets.map(t =>
        t.dbId === ticket.dbId ? { ...t, priority: newPriority, updatedAt: new Date().toLocaleString() } : t
      );
      setTickets(updatedTickets);

      if (selectedTicket?.dbId === ticket.dbId) {
        setSelectedTicket({ ...selectedTicket, priority: newPriority });
      }

      Swal.fire({
        title: 'Priority Updated!',
        html: `Ticket priority changed to <strong>${newPriority}</strong>`,
        icon: 'success',
        confirmButtonColor: '#1e3a5f',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Failed to update priority:", err);
      Swal.fire({ title: 'Error', text: 'Could not update priority. Please try again.', icon: 'error', confirmButtonColor: '#ea580c' });
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      Swal.fire({
        title: 'Empty Message',
        text: 'Please type a message before sending.',
        icon: 'warning',
        confirmButtonColor: '#ea580c'
      });
      return;
    }

    try {
      const res = await api.post(`/support-tickets/${selectedTicket.dbId}/reply`, { message: replyMessage });

      const newReply = {
        from: res.data.from,
        message: res.data.message,
        time: new Date(res.data.created_at).toLocaleString(),
      };

      const updatedTickets = tickets.map(t =>
        t.dbId === selectedTicket.dbId
          ? { ...t, replies: [...t.replies, newReply], updatedAt: new Date().toLocaleString() }
          : t
      );

      setTickets(updatedTickets);
      setSelectedTicket({
        ...selectedTicket,
        replies: [...selectedTicket.replies, newReply]
      });
      setReplyMessage("");

      Swal.fire({
        title: 'Reply Sent!',
        text: 'Your message has been sent successfully.',
        icon: 'success',
        confirmButtonColor: '#16a34a',
        timer: 2000
      });
    } catch (err) {
      console.error("Failed to send reply:", err);
      Swal.fire({ title: 'Error', text: 'Could not send reply. Please try again.', icon: 'error', confirmButtonColor: '#ea580c' });
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High": return "admin-st-priority-high";
      case "Medium": return "admin-st-priority-medium";
      case "Low": return "admin-st-priority-low";
      default: return "";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Open": return "admin-st-status-open";
      case "In Progress": return "admin-st-status-progress";
      case "Resolved": return "admin-st-status-resolved";
      case "Closed": return "admin-st-status-closed";
      default: return "";
    }
  };

  return (
    <div className="admin-st-wrapper">
      <div className="admin-st-container">
        {/* Header */}
        <div className="admin-st-header">
          <div>
            <h1 className="admin-st-title">Support Tickets</h1>
            <p className="admin-st-subtitle">Manage and respond to user support requests</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="admin-st-stats-grid">
          <div className="admin-st-stat-card">
            <div className="admin-st-stat-icon admin-st-stat-blue">
              <MessageSquare size={24} />
            </div>
            <div>
              <div className="admin-st-stat-value">{stats.total}</div>
              <div className="admin-st-stat-label">Total Tickets</div>
            </div>
          </div>
          <div className="admin-st-stat-card">
            <div className="admin-st-stat-icon admin-st-stat-orange">
              <Clock size={24} />
            </div>
            <div>
              <div className="admin-st-stat-value">{stats.open}</div>
              <div className="admin-st-stat-label">Open</div>
            </div>
          </div>
          <div className="admin-st-stat-card">
            <div className="admin-st-stat-icon admin-st-stat-purple">
              <AlertCircle size={24} />
            </div>
            <div>
              <div className="admin-st-stat-value">{stats.inProgress}</div>
              <div className="admin-st-stat-label">In Progress</div>
            </div>
          </div>
          <div className="admin-st-stat-card">
            <div className="admin-st-stat-icon admin-st-stat-green">
              <CheckCircle size={24} />
            </div>
            <div>
              <div className="admin-st-stat-value">{stats.resolved}</div>
              <div className="admin-st-stat-label">Resolved</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="admin-st-main-card">
          {/* Tabs */}
          <div className="admin-st-tabs">
            {["All", "Open", "In Progress", "Resolved", "Closed"].map(tab => (
              <button
                key={tab}
                className={`admin-st-tab ${activeTab === tab ? "admin-st-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="admin-st-filters">
            <span className="admin-st-filter-label">Filter by:</span>
            <select
              className="admin-st-filter-select"
              value={filterCategory}
              onChange={e => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Categories</option>
              <option>Booking Issue</option>
              <option>Technical Issue</option>
              <option>Payment Issue</option>
              <option>Account Issue</option>
            </select>
            <select
              className="admin-st-filter-select"
              value={filterPriority}
              onChange={e => {
                setFilterPriority(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Priorities</option>
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
            <div className="admin-st-search-wrapper">
              <Search size={18} className="admin-st-search-icon" />
              <input
                type="text"
                placeholder="Search tickets..."
                className="admin-st-search-input"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Content Grid */}
          <div className="admin-st-content-grid">
            {/* Left: Tickets List */}
            <div className="admin-st-list-section">
              <h3 className="admin-st-section-title">All Tickets</h3>

              {ticketsLoading ? (
                <div className="admin-st-empty-state">
                  <MessageSquare size={48} />
                  <p>Loading tickets...</p>
                </div>
              ) : ticketsError ? (
                <div className="admin-st-empty-state">
                  <XCircle size={48} />
                  <p>{ticketsError}</p>
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="admin-st-empty-state">
                  <MessageSquare size={48} />
                  <p>No tickets found</p>
                </div>
              ) : (
                <>
                  <div className="admin-st-table-container">
                    <table className="admin-st-table">
                      <thead>
                        <tr>
                          <th className="admin-st-th">Ticket ID</th>
                          <th className="admin-st-th">Subject</th>
                          <th className="admin-st-th">Priority</th>
                          <th className="admin-st-th">Status</th>
                          <th className="admin-st-th">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTickets.map(ticket => (
                          <tr key={ticket.dbId} className="admin-st-tr">
                            <td className="admin-st-td">
                              <span className="admin-st-ticket-id">{ticket.id}</span>
                            </td>
                            <td className="admin-st-td">
                              <div className="admin-st-subject-cell">
                                <span className="admin-st-subject">{ticket.subject}</span>
                                <span className="admin-st-user">by {ticket.user}</span>
                              </div>
                            </td>
                            <td className="admin-st-td">
                              <select
                                className={`admin-st-priority-dropdown ${getPriorityColor(ticket.priority)}`}
                                value={ticket.priority}
                                onChange={(e) => handlePriorityChange(ticket, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                              </select>
                            </td>
                            <td className="admin-st-td">
                              <span className={`admin-st-status-badge ${getStatusColor(ticket.status)}`}>
                                {ticket.status}
                              </span>
                            </td>
                            <td className="admin-st-td">
                              <button
                                className="admin-st-btn-view"
                                onClick={() => setSelectedTicket(ticket)}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="admin-st-pagination">
                    <div className="admin-st-pagination-info">
                      Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredTickets.length)} of {filteredTickets.length} tickets
                    </div>
                    <div className="admin-st-pagination-controls">
                      <button
                        className="admin-st-pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              className={`admin-st-pagination-number ${currentPage === page ? "admin-st-pagination-number-active" : ""}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 8px', color: '#94a3b8' }}>...</span>;
                        }
                        return null;
                      })}

                      <button
                        className="admin-st-pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right: Ticket Details */}
            <div className="admin-st-details-panel">
              {selectedTicket ? (
                <div className="admin-st-details-content">
                  <div className="admin-st-details-header">
                    <h2 className="admin-st-details-title">{selectedTicket.subject}</h2>
                    <span className="admin-st-ticket-id-badge">{selectedTicket.id}</span>
                  </div>

                  <div className="admin-st-ticket-meta">
                    <div className="admin-st-meta-item">
                      <User size={16} />
                      <div>
                        <div className="admin-st-meta-label">Submitted by</div>
                        <div className="admin-st-meta-value">{selectedTicket.user} ({selectedTicket.userType})</div>
                      </div>
                    </div>
                    <div className="admin-st-meta-item">
                      <Calendar size={16} />
                      <div>
                        <div className="admin-st-meta-label">Created</div>
                        <div className="admin-st-meta-value">{selectedTicket.createdAt}</div>
                      </div>
                    </div>
                    <div className="admin-st-meta-item">
                      <Tag size={16} />
                      <div>
                        <div className="admin-st-meta-label">Category</div>
                        <div className="admin-st-meta-value">{selectedTicket.category}</div>
                      </div>
                    </div>
                  </div>

                  <div className="admin-st-description-section">
                    <h3 className="admin-st-section-subtitle">Description</h3>
                    <p className="admin-st-description">{selectedTicket.description}</p>
                  </div>

                  <div className="admin-st-replies-section">
                    <h3 className="admin-st-section-subtitle">Conversation ({selectedTicket.replies.length})</h3>
                    <div className="admin-st-replies-list">
                      {selectedTicket.replies.length === 0 ? (
                        <p className="admin-st-no-replies">No replies yet. Be the first to respond!</p>
                      ) : (
                        selectedTicket.replies.map((reply, index) => (
                          <div key={index} className="admin-st-reply-item">
                            <div className="admin-st-reply-header">
                              <span className="admin-st-reply-from">{reply.from}</span>
                              <span className="admin-st-reply-time">{reply.time}</span>
                            </div>
                            <p className="admin-st-reply-message">{reply.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {selectedTicket.status !== "Closed" && (
                    <div className="admin-st-reply-form">
                      <textarea
                        className="admin-st-reply-textarea"
                        placeholder="Type your reply here..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        rows={4}
                      />
                      <button className="admin-st-btn-send" onClick={handleSendReply}>
                        <Send size={18} />
                        Send Reply
                      </button>
                    </div>
                  )}

                  <div className="admin-st-status-actions">
                    <h3 className="admin-st-section-subtitle">Change Status</h3>
                    <div className="admin-st-status-buttons">
                      <button
                        className="admin-st-btn-status admin-st-btn-progress"
                        onClick={() => handleStatusChange(selectedTicket, "In Progress")}
                        disabled={selectedTicket.status === "In Progress"}
                      >
                        Mark In Progress
                      </button>
                      <button
                        className="admin-st-btn-status admin-st-btn-resolved"
                        onClick={() => handleStatusChange(selectedTicket, "Resolved")}
                        disabled={selectedTicket.status === "Resolved"}
                      >
                        Mark Resolved
                      </button>
                      <button
                        className="admin-st-btn-status admin-st-btn-closed"
                        onClick={() => handleStatusChange(selectedTicket, "Closed")}
                        disabled={selectedTicket.status === "Closed"}
                      >
                        Close Ticket
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="admin-st-no-selection">
                  <MessageSquare size={48} />
                  <p>Select a ticket to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTickets;